import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, CreditCard } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

// Public "Pay your account" page — customer picks which outstanding invoices
// to pay (one, some, or all) and checks out once via Stripe.
export default function PayAccountPage() {
  const { customerId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState({});
  const [paying, setPaying] = useState(false);
  const banner = new URLSearchParams(window.location.search).get('status');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/payments/account/${customerId}`);
        if (!res.ok) throw new Error('not found');
        const d = await res.json();
        setData(d);
        const sel = {};
        (d.invoices || []).forEach((i) => { sel[i.id] = true; });
        setSelected(sel);
      } catch {
        setError('We could not load your account. Please contact us.');
      }
    })();
  }, [customerId]);

  const chosen = data ? (data.invoices || []).filter((i) => selected[i.id]) : [];
  const total = chosen.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);

  const pay = async () => {
    setPaying(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/account/${customerId}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_ids: chosen.map((i) => i.id) }),
      });
      if (!res.ok) throw new Error('checkout failed');
      const d = await res.json();
      window.location.href = d.url;
    } catch {
      setError('Could not start the payment — please try again or use bank transfer below.');
      setPaying(false);
    }
  };

  if (error && !data) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <p className="text-zinc-600">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="animate-spin text-zinc-400" size={32} />
      </div>
    );
  }

  const brand = data.brand_color || '#10b981';

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="pay-account-page">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="px-6 py-6 text-center border-b border-zinc-100">
          <h1 className="text-2xl font-bold" style={{ color: brand }}>{data.business_name}</h1>
          <p className="text-sm text-zinc-500 mt-1">Pay your account</p>
          <p className="text-sm font-medium text-zinc-700">{data.customer_name}</p>
        </div>

        {banner === 'success' && (
          <div className="m-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-emerald-700 text-sm" data-testid="pay-success-banner">
            <CheckCircle2 size={18} /> Payment received — thank you! Your account will update shortly.
          </div>
        )}
        {banner === 'cancelled' && (
          <div className="m-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2 text-amber-700 text-sm" data-testid="pay-cancelled-banner">
            <XCircle size={18} /> Payment cancelled — nothing was charged.
          </div>
        )}

        <div className="p-5">
          {(data.invoices || []).length === 0 ? (
            <div className="text-center py-10" data-testid="pay-account-clear">
              <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-3" />
              <p className="font-semibold text-zinc-800">Nothing outstanding</p>
              <p className="text-sm text-zinc-500">Your account is fully up to date.</p>
            </div>
          ) : (
            <>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
                Select invoices to pay
              </p>
              <div className="space-y-2 mb-4">
                {data.invoices.map((inv) => (
                  <label
                    key={inv.id}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      selected[inv.id] ? 'border-emerald-300 bg-emerald-50/60' : 'border-zinc-200 bg-white'
                    }`}
                    data-testid={`pay-invoice-${inv.invoice_number}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!selected[inv.id]}
                      onChange={(e) => setSelected({ ...selected, [inv.id]: e.target.checked })}
                      className="mt-1 w-5 h-5 accent-emerald-600"
                      data-testid={`pay-invoice-checkbox-${inv.invoice_number}`}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="flex justify-between gap-2">
                        <span className="font-semibold text-zinc-900 text-sm">{inv.invoice_number}</span>
                        <span className="font-bold text-zinc-900 text-sm">£{(parseFloat(inv.total) || 0).toFixed(2)}</span>
                      </span>
                      <span className="block text-xs text-zinc-500">{fmtDate(inv.created_at)}</span>
                      {inv.notes && <span className="block text-xs text-amber-700 italic mt-0.5">{inv.notes}</span>}
                    </span>
                  </label>
                ))}
              </div>

              {data.stripe_enabled && (
                <button
                  onClick={pay}
                  disabled={chosen.length === 0 || paying}
                  className="w-full py-4 rounded-xl text-white font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-40 transition-opacity"
                  style={{ background: brand }}
                  data-testid="pay-account-btn"
                >
                  {paying ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
                  {chosen.length === 0 ? 'Select an invoice' : `Pay £${total.toFixed(2)}`}
                </button>
              )}
              {error && <p className="text-xs text-red-600 mt-2 text-center">{error}</p>}

              {data.bank && (
                <div className="mt-5 bg-slate-50 rounded-xl p-4" data-testid="pay-account-bank">
                  <p className="text-sm font-semibold text-zinc-700 mb-2">Prefer bank transfer?</p>
                  <div className="text-sm text-zinc-600 space-y-1">
                    <p className="flex justify-between"><span>Account name</span><span className="font-medium text-zinc-800">{data.bank.account_name}</span></p>
                    <p className="flex justify-between"><span>Sort code</span><span className="font-medium text-zinc-800">{data.bank.sort_code}</span></p>
                    <p className="flex justify-between"><span>Account number</span><span className="font-medium text-zinc-800">{data.bank.account_number}</span></p>
                    <p className="flex justify-between"><span>Reference</span><span className="font-medium text-zinc-800">{(data.invoices[0] || {}).invoice_number}</span></p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
